import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, Loader2, CheckCircle2, ScanLine, AlertTriangle, RotateCcw, Send } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

export default function MobileScan() {
  const { sessionId } = useParams();
  const { t } = useLang();
  const [status, setStatus] = useState("capture"); // capture | sending | done | error
  const [sides, setSides] = useState({ recto: null, verso: null });
  const camRef = useRef(null);
  const captureSide = useRef("recto");

  const needSide = !sides.recto ? "recto" : !sides.verso ? "verso" : null;

  const openCamera = (side) => { captureSide.current = side; camRef.current?.click(); };

  const onPick = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSides((s) => ({ ...s, [captureSide.current]: reader.result }));
    reader.readAsDataURL(file);
    if (camRef.current) camRef.current.value = "";
  };

  const send = async () => {
    if (!sides.recto || !sides.verso) return;
    setStatus("sending");
    try {
      await api.post(`/scan-sessions/${sessionId}/scan`, { images: [sides.recto, sides.verso] });
      setStatus("done");
    } catch (e) {
      setStatus("error");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const retryAll = () => { setSides({ recto: null, verso: null }); setStatus("capture"); };

  const SIDES = [
    { key: "recto", label: t("scanner.side_recto"), instr: t("scanner.mobile_recto") },
    { key: "verso", label: t("scanner.side_verso"), instr: t("scanner.mobile_verso") },
  ];

  return (
    <div className="noise-overlay bg-[#050505] min-h-screen flex flex-col" data-testid="mobile-scan-page">
      <header className="px-5 h-[68px] flex items-center border-b border-white/10"><Logo /></header>
      <main className="flex-1 flex flex-col items-center px-6 py-10 text-center">
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="mobile-scan-input" onChange={(e) => onPick(e.target.files?.[0])} />

        {status === "capture" && (
          <div className="w-full max-w-sm flex flex-col items-center gap-6">
            <div className="ia-badge-glow inline-flex items-center gap-1.5 border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-2.5 py-1">
              <ScanLine className="w-3 h-3 text-[#00E5FF]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]">Scan IA</span>
            </div>
            <h1 className="font-display font-900 text-white text-2xl tracking-tight uppercase">{t("scanner.mobile_title")}</h1>

            {/* progress dots */}
            <div className="flex items-center gap-2">
              {SIDES.map((s) => (
                <span key={s.key} className={`h-2 w-10 rounded-full ${sides[s.key] ? "bg-emerald-400" : "bg-white/15"}`} />
              ))}
            </div>

            <div className="w-full grid grid-cols-2 gap-3">
              {SIDES.map((s) => (
                <div key={s.key} className="border border-white/12 bg-[#0C0C0E] p-2" data-testid={`mobile-side-${s.key}`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <span className="font-mono-tech text-[9px] uppercase tracking-[0.12em] text-[#00E5FF]">{s.label}</span>
                    {sides[s.key] && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </div>
                  {sides[s.key] ? (
                    <div className="relative">
                      <img src={sides[s.key]} alt={s.key} className="w-full h-28 object-cover border border-white/10" />
                      <button onClick={() => openCamera(s.key)} data-testid={`mobile-retake-${s.key}`} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 py-2 border border-white/20 text-white font-mono-tech text-[10px] uppercase tracking-[0.12em]">
                        <RotateCcw className="w-3.5 h-3.5" /> {t("scanner.mobile_retake")}
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => openCamera(s.key)} data-testid={`mobile-take-${s.key}`} className="w-full h-[136px] flex flex-col items-center justify-center gap-2 border border-dashed border-[#00E5FF]/30 hover:border-[#00E5FF] transition-colors">
                      <Camera className="w-7 h-7 text-[#00E5FF]" />
                      <span className="font-mono-tech text-[9px] uppercase tracking-[0.12em] text-white px-1">{t("scanner.cap_photo")}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <p className="text-zinc-500 text-xs max-w-xs">{needSide ? SIDES.find((s) => s.key === needSide)?.instr : t("scanner.mobile_hint")}</p>

            <button onClick={send} disabled={!!needSide} data-testid="mobile-send" className="w-full inline-flex items-center justify-center gap-2 py-4 bg-[#E60000] text-white font-display font-700 uppercase tracking-wide text-sm disabled:opacity-40 disabled:cursor-not-allowed">
              <Send className="w-4 h-4" /> {t("scanner.mobile_send_both")}
            </button>
          </div>
        )}

        {status === "sending" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-[#00E5FF]" data-testid="mobile-sending">
            <Loader2 className="w-10 h-10 animate-spin" />
            <p className="font-mono-tech text-sm uppercase tracking-[0.2em]">{t("scanner.mobile_sending")}</p>
          </div>
        )}

        {status === "done" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 fade-up" data-testid="mobile-done">
            <div className="w-20 h-20 rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/10 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-[#00E5FF]" /></div>
            <h2 className="font-display font-900 text-white text-2xl uppercase">{t("scanner.mobile_done")}</h2>
            <p className="text-zinc-400 text-sm max-w-xs">{t("scanner.mobile_done_desc")}</p>
          </div>
        )}

        {status === "error" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4" data-testid="mobile-error">
            <AlertTriangle className="w-10 h-10 text-[#E60000]" />
            <p className="text-white font-display font-700">{t("scanner.mobile_error")}</p>
            <button onClick={retryAll} className="mt-2 px-6 py-3 border border-white/20 text-white text-sm uppercase tracking-wide" data-testid="mobile-retry">{t("scanner.mobile_retry")}</button>
          </div>
        )}
      </main>
    </div>
  );
}
